import React, { useState, useEffect } from "react";
import { Box, CssBaseline, Drawer, Toolbar } from "@mui/material";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import ParentAttendance from "../pages/Attendance";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";

const drawerWidth = 240;

const ParentDashboard = ({ content }) => {
  const location = useLocation();
  const [openFeeModal, setOpenFeeModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [availablePages, setAvailablePages] = useState([]);

  // Fetch available pages from API
  useEffect(() => {
    const fetchAvailablePages = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");

        const response = await fetch(`${apiUrl}/ExclusivePermission/GetSearchForm`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const parentPortalPages = data.filter(
            item => item.moduleName === "Parents Portal" && item.isPermission === 1
          );
          
          setAvailablePages(parentPortalPages);
        }
      } catch (error) {
        console.error("Error fetching available pages:", error);
      }
    };

    fetchAvailablePages();
  }, []);

  useEffect(() => {
    const fetchFeeDetails = async () => {
      try {
        const studentID = sessionStorage.getItem("employeeId");
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");

        const response = await fetch(`${apiUrl}/Student/FetchStudentPaymentDetails`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ studentID }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch fee details: ${response.status}`);
        }

        const data = await response.json();
       const dueFeesUpToCurrentMonth = data.filter(
  (fee) =>
    fee.paymentStatus === "Overdue" 
   
);


        const currentMonthDate = new Date();
currentMonthDate.setDate(1);
currentMonthDate.setHours(0, 0, 0, 0);



     if (dueFeesUpToCurrentMonth.length > 0) {
  const totalDue = dueFeesUpToCurrentMonth.reduce((sum, fee) => sum + fee.dueAmount, 0);
  const months = dueFeesUpToCurrentMonth.map((fee) => fee.paymentForMonth).join(", ");
          setSnackbarMessage(
            `Fee for ${months} is overdue. Total due: ₹${totalDue.toFixed(2)}`
          );
          setOpenFeeModal(true);
        }
      } catch (error) {
        console.error("Error fetching fee details:", error);
      }
    };

    fetchFeeDetails();
  }, []);

  // Auto-close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  // Normalize URL for comparison
  const normalizeUrl = (url) => {
    return url.replace(/^(\.\.\/)*/, '').replace(/^\//, '');
  };

 // Pages every parent can open, regardless of the permission API
const ALWAYS_ALLOWED = [
  "parentdashboard",
  "parent-notifications",
];

const checkPermission = () => {
  const currentPath = normalizeUrl(location.pathname)
    .replace(/\/$/, "")
    .toLowerCase();

  if (ALWAYS_ALLOWED.includes(currentPath)) return true;

  return availablePages.some(
    (page) =>
      normalizeUrl(page.formUrl).replace(/\/$/, "").toLowerCase() === currentPath
  );
};

  const hasPermission = checkPermission();

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Header */}
      <Header setIsSidebarOpen={setIsSidebarOpen} />

      {/* Sidebar */}
      <Drawer
        variant={isSidebarOpen ? "permanent" : "temporary"}
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            color: "#fff",
            marginTop: "64px",
          },
        }}
      >
        <Sidebar availablePages={availablePages} />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: 2,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {hasPermission ? (
          content || <ParentAttendance />
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5">Access Denied</Typography>
            <Typography>You do not have permission to access this page.</Typography>
          </Box>
        )}
      </Box>

      {/* Fee Modal */}
      <Modal
        open={openFeeModal}
        onClose={() => setOpenFeeModal(false)}
        aria-labelledby="fee-modal-title"
        aria-describedby="fee-modal-description"
        sx={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          "& .MuiModal-backdrop": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '90%', sm: 420 },
            maxWidth: 440,
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(220, 38, 38, 0.3)",
            overflow: "hidden",
            outline: 'none',
            border: "2px solid #ef4444",
          }}
        >
          {/* Warning Icon Section with Red Gradient */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
              py: 3,
              px: 3,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative circles */}
            <Box
              sx={{
                position: "absolute",
                top: -15,
                right: -15,
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.08)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -20,
                left: -20,
                width: 90,
                height: 90,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.08)",
              }}
            />

            {/* Warning Icon */}
            <Box
              sx={{
                position: "relative",
                width: 70,
                height: 70,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                border: "3px solid rgba(255, 255, 255, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255, 255, 255, 0.4)" },
                  "50%": { transform: "scale(1.05)", boxShadow: "0 0 0 8px rgba(255, 255, 255, 0)" },
                },
              }}
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </Box>

            <h2
              id="fee-modal-title"
              style={{
                margin: 0,
                color: "white",
                fontSize: "22px",
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: "-0.3px",
              }}
            >
              Fee Payment Overdue
            </h2>
          </Box>

          {/* Content Section */}
          <Box sx={{ px: 3, pt: 3, pb: 3 }}>
            {/* Warning Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: "#fee2e2",
                color: "#991b1b",
                px: 2,
                py: 0.75,
                borderRadius: 2,
                fontSize: "12px",
                fontWeight: 600,
                margin: "0 auto 16px",
                display: "flex",
                justifyContent: "center",
                width: "fit-content",
                marginLeft: "auto",
                marginRight: "auto",
                border: "1px solid #fecaca",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              URGENT ACTION REQUIRED
            </Box>

            <p
              id="fee-modal-description"
              style={{
                margin: "0 0 24px 0",
                color: "#666",
                fontSize: "14px",
                lineHeight: "1.6",
                textAlign: "center",
              }}
            >
              {snackbarMessage}
            </p>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
              <button
                onClick={() => setOpenFeeModal(false)}
                style={{
                  width: "100%",
                  padding: "13px 24px",
                  background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.4)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 38, 38, 0.5)";
                  e.currentTarget.style.background = "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 38, 38, 0.4)";
                  e.currentTarget.style.background = "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)";
                }}
              >
                OKAY
              </button>
            </Box>
          </Box>

          {/* Bottom Red Accent Line */}
          <Box
            sx={{
              height: 3,
              background: "linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)",
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ParentDashboard;